import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';

import { fetchWrapper } from '../../helpers';
import { getSerializedErrorMessage } from '../../utils/errors';
import { TREATMENT_PLANS_V2_API_BASE } from './constants';
import {
  addTreatmentPlanV3AssessmentCases,
  createTreatmentPlanV3AssessmentThunks,
} from './pages/assessment';
import { addTreatmentPlanV3ChildCases, createTreatmentPlanV3ChildThunks } from './pages/child';
import { addTreatmentPlanV3CrisisCases, createTreatmentPlanV3CrisisThunks } from './pages/crisis';
import {
  addTreatmentPlanV3DischargeCases,
  createTreatmentPlanV3DischargeThunks,
} from './pages/discharge';
import {
  addTreatmentPlanV3EducationCases,
  createTreatmentPlanV3EducationThunks,
} from './pages/education';
import { addTreatmentPlanV3FamilyCases, createTreatmentPlanV3FamilyThunks } from './pages/family';
import {
  addTreatmentPlanV3InformationCases,
  createTreatmentPlanV3InformationThunks,
} from './pages/information';
import {
  addTreatmentPlanV3MedicationCases,
  createTreatmentPlanV3MedicationThunks,
} from './pages/medication';
import {
  addTreatmentPlanV3MedicalCases,
  createTreatmentPlanV3MedicalThunks,
} from './pages/medical';
import {
  addTreatmentPlanV3ReferralCases,
  createTreatmentPlanV3ReferralThunks,
} from './pages/referral';
import {
  addTreatmentPlanV3TherapyCases,
  createTreatmentPlanV3TherapyThunks,
} from './pages/therapy';
import {
  addTreatmentPlanV3TreatmentCases,
  createTreatmentPlanV3TreatmentThunks,
} from './pages/treatment';

import type { ActionReducerMapBuilder } from '@reduxjs/toolkit';
import type { ITreatmentPlan } from '../../utils/types';
import type { CreateTreatmentPlanV3Payload, TreatmentPlanV3State } from './types';

const initialState = createInitialState();
const reducers = createReducers();
const extraActions = createExtraActions();
const slice = createSlice({
  name: 'treatmentPlanV3',
  initialState,
  reducers,
  extraReducers: (builder: ActionReducerMapBuilder<TreatmentPlanV3State>) =>
    createExtraReducers(builder),
});

function createInitialState(): TreatmentPlanV3State {
  return {
    createTreatmentPlanLoading: false,
    informationPage: null,
    informationPageLoading: false,
    informationPageError: null,
    informationPageRequestId: null,
    referralPage: null,
    referralPageLoading: false,
    referralPageError: null,
    referralPageRequestId: null,
    referralPageTreatmentPlanId: null,
    referralPageChangedSinceRequest: false,
    createReferralLoading: false,
    updateReferralLoading: false,
    deleteReferralLoading: false,
    medicationPage: null,
    medicationPageLoading: false,
    medicationPageError: null,
    medicationPageRequestId: null,
    medicationPageTreatmentPlanId: null,
    medicationPageChangedSinceRequest: false,
    createMedicationLoading: false,
    updateMedicationLoading: false,
    deleteMedicationLoading: false,
    deleteAllMedicationsLoading: false,
    setNoMedicationLoading: false,
    medicalPage: null,
    medicalPageLoading: false,
    medicalPageError: null,
    medicalPageRequestId: null,
    medicalPageTreatmentPlanId: null,
    medicalPageChangedSinceRequest: false,
    updateMedicalInfoLoading: false,
    createMedicalDiagnosisLoading: false,
    updateMedicalDiagnosisLoading: false,
    deleteMedicalDiagnosisLoading: false,
    createMedicalSelectionLoading: false,
    deleteMedicalSelectionLoading: false,
    therapyPage: null,
    therapyPageLoading: false,
    therapyPageError: null,
    therapyPageRequestId: null,
    therapyPageTreatmentPlanId: null,
    createTherapyLoading: false,
    updateTherapyLoading: false,
    deleteTherapyLoading: false,
    setNoTherapyLoading: false,
    educationPage: null,
    educationPageLoading: false,
    educationPageError: null,
    educationPageRequestId: null,
    educationPageTreatmentPlanId: null,
    updateEducationInfoLoading: false,
    setNotAttendingSchoolLoading: false,
    childPage: null,
    childPageLoading: false,
    childPageError: null,
    childPageRequestId: null,
    childPageTreatmentPlanId: null,
    updateChildInfoLoading: false,
    createChildBackgroundLoading: false,
    deleteChildBackgroundLoading: false,
    createChildScheduleLoading: false,
    deleteChildScheduleLoading: false,
    crisisPage: null,
    crisisPageLoading: false,
    crisisPageError: null,
    crisisPageRequestId: null,
    crisisPageTreatmentPlanId: null,
    crisisMutationLoadRequestIds: {},
    createCrisisPlanLoading: false,
    updateCrisisPlanLoading: false,
    deleteCrisisPlanLoading: false,
    deleteAllCrisisPlansLoading: false,
    setNoCrisisPlanNeededLoading: false,
    dischargePage: null,
    dischargePageLoading: false,
    dischargePageError: null,
    dischargePageRequestId: null,
    dischargePageTreatmentPlanId: null,
    dischargePageChangedSinceRequest: false,
    dischargePageLoadCount: 0,
    updateDischargeCriteriaLoading: false,
    createDischargeReasonLoading: false,
    deleteDischargeReasonLoading: false,
    treatmentPage: null,
    treatmentPageLoading: false,
    treatmentPageError: null,
    treatmentPageRequestId: null,
    treatmentPageTreatmentPlanId: null,
    treatmentPagePatchesSinceRequest: [],
    treatmentPageLoadCount: 0,
    updateTreatmentPageLoading: false,
    familyPage: null,
    familyPageLoading: false,
    familyPageError: null,
    familyPageRequestId: null,
    familyPageTreatmentPlanId: null,
    familyPageChangedSinceRequest: false,
    updateFamilyLoading: false,
    createFamilyMemberLoading: false,
    updateFamilyMemberLoading: false,
    deleteFamilyMemberLoading: false,
    createClientSiblingLoading: false,
    updateClientSiblingLoading: false,
    deleteClientSiblingLoading: false,
    createFamilyAdditionalInfoLoading: false,
    deleteFamilyAdditionalInfoLoading: false,
    assessmentPage: null,
    assessmentPageLoading: false,
    assessmentPageError: null,
    assessmentPageRequestId: null,
    assessmentPageTreatmentPlanId: null,
    assessmentPageChangedSinceRequest: false,
    assessmentTemplates: null,
    assessmentTemplatesLoading: false,
    assessmentTemplatesError: null,
    assessmentTemplatesRequestId: null,
    assessmentTemplatesTreatmentPlanId: null,
    assessmentReadinessItemRequestIds: {},
    updateAssessmentPageLoading: false,
    createAssessmentBehaviorLoading: false,
    updateAssessmentBehaviorLoading: false,
    deleteAssessmentBehaviorLoading: false,
    createAssessmentMethodLoading: false,
    updateAssessmentMethodLoading: false,
    deleteAssessmentMethodLoading: false,
    updateAssessmentReadinessItemLoading: false,
    createAssessmentMethodVisualsLoading: false,
    updateAssessmentMethodVisualLoading: false,
    updateAssessmentMethodVisualFileLoading: false,
    deleteAssessmentMethodVisualLoading: false,
    mutationRequestIds: {},
    error: null,
  };
}

// No synchronous reducers yet — kept for the remaining v3 pages.
function createReducers() {
  return {};
}

function createExtraActions() {
  return {
    createTreatmentPlanV3: createTreatmentPlanV3(),
    ...createTreatmentPlanV3InformationThunks(),
    ...createTreatmentPlanV3ReferralThunks(),
    ...createTreatmentPlanV3MedicationThunks(),
    ...createTreatmentPlanV3MedicalThunks(),
    ...createTreatmentPlanV3TherapyThunks(),
    ...createTreatmentPlanV3EducationThunks(),
    ...createTreatmentPlanV3ChildThunks(),
    ...createTreatmentPlanV3CrisisThunks(),
    ...createTreatmentPlanV3DischargeThunks(),
    ...createTreatmentPlanV3TreatmentThunks(),
    ...createTreatmentPlanV3FamilyThunks(),
    ...createTreatmentPlanV3AssessmentThunks(),
  };
}

function createTreatmentPlanV3() {
  return createAsyncThunk<ITreatmentPlan, CreateTreatmentPlanV3Payload>(
    'treatmentPlanV3/createTreatmentPlanV3',
    async ({ data, getAccessTokenSilently }) =>
      fetchWrapper.post({ url: `${TREATMENT_PLANS_V2_API_BASE}/`, getAccessTokenSilently }, data),
  );
}

function createExtraReducers(builder: ActionReducerMapBuilder<TreatmentPlanV3State>) {
  addCreateTreatmentPlanV3Cases(builder);
  addTreatmentPlanV3InformationCases(builder, extraActions);
  addTreatmentPlanV3ReferralCases(builder, extraActions);
  addTreatmentPlanV3MedicationCases(builder, extraActions);
  addTreatmentPlanV3MedicalCases(builder, extraActions);
  addTreatmentPlanV3TherapyCases(builder, extraActions);
  addTreatmentPlanV3EducationCases(builder, extraActions);
  addTreatmentPlanV3ChildCases(builder, extraActions);
  addTreatmentPlanV3CrisisCases(builder, extraActions);
  addTreatmentPlanV3DischargeCases(builder, extraActions);
  addTreatmentPlanV3TreatmentCases(builder, extraActions);
  addTreatmentPlanV3FamilyCases(builder, extraActions);
  addTreatmentPlanV3AssessmentCases(builder, extraActions);
}

function addCreateTreatmentPlanV3Cases(builder: ActionReducerMapBuilder<TreatmentPlanV3State>) {
  builder
    .addCase(extraActions.createTreatmentPlanV3.pending, (state) => {
      state.createTreatmentPlanLoading = true;
      state.error = null;
    })
    .addCase(extraActions.createTreatmentPlanV3.fulfilled, (state) => {
      state.createTreatmentPlanLoading = false;
    })
    .addCase(extraActions.createTreatmentPlanV3.rejected, (state, action) => {
      state.createTreatmentPlanLoading = false;
      state.error = action.error;
      toast.error(getSerializedErrorMessage(action.error));
    });
}

export const treatmentPlanV3Actions = { ...slice.actions, ...extraActions };
export const treatmentPlanV3Reducer = slice.reducer;
